// Core
import React from "react";
import { connect } from "react-redux";
import fileDownload from "downloadjs";
import { cloneDeep } from "lodash";
import { json2csv } from "json-2-csv/src/converter";

// Components
import TemplateList from "./TemplatesList";
import TemplatesEditor from "./TemplatesEditor";
import TemplateCreator from "./TemplateCreator";
import TemplatesMenuCategories from "./TemplatesMenuCategories";
import TitleEditor from "./TitleEditor";
import TemplatesSettings from "./TemplatesSettings";

// Actions
import { track } from "../../redux/auth/actions";
import {
  addTemplate,
  getTemplatest,
  editTemplate,
  deleteTemplate,
  copyTemplate,
  editTemplateType,
  uploadImage
} from "../../redux/templates/actions";

// Selectors
import * as authSelectors from "../../redux/auth/selectors";
import * as templatesSelectors from "../../redux/templates/selectors";

// Styles & assets
import "./Templates.css";

const REPORT_TYPES = [
  { label: "Detailed Format", value: "project" },
  { label: "Table Format", value: "table" }
];

const defaultFieldsTable = {
  issueNumber: true,
  location: true,
  description: true,
  assignTo: true,
  dueDate: true,
  cost: false,
  schedule: false,
  status: false,
  images: true
};

const defaultFieldsDetailed = {
  issueNumber: true,
  issueTitle: true,
  location: true,
  description: true,
  assignTo: true,
  dueDate: true,
  cost: true,
  schedule: true,
  status: true,
  images: true
};

const FIELDS_BY_TYPE = {
  project: defaultFieldsDetailed,
  table: defaultFieldsTable
};

const mapStateToProps = state => ({
  templates: templatesSelectors.templates(state),
  userId: authSelectors.getUserId(state)
});

const mapDispatchToProps = {
  addTemplate,
  getTemplatest,
  editTemplate,
  deleteTemplate,
  copyTemplate,
  editTemplateType,
  uploadImage,
  track
};

class Templates extends React.Component {
  state = {
    activeTemplate: null,
    activeTemplateByEditTitle: null,
    category: "title"
  };

  componentDidMount() {
    const { userId } = JSON.parse(localStorage.getItem("auth0")) || "";
    this.props.getTemplatest(userId);
    this.props.track("Web app: Templates tab chosen");
  }

  onAddTemplate = ({ title, titleText = "", typeReport = "project" }) => {
    const { userId, addTemplate, getTemplatest } = this.props;
    if (title.length === 0) title = "my template";
    const empty = {
      id: 0,
      title,
      text: {
        title: titleText,
        opening: "<p></p>",
        closing: "<p></p>"
      },
      typeReport: typeReport,
      logoId: null,
      reportFields: { ...FIELDS_BY_TYPE[typeReport] }
    };
    addTemplate(userId, empty).then(() => {
      track("Web app: Template created");
      getTemplatest(userId);
    });
  };

  deleteImage = () => {
    const { editTemplate, userId, getTemplatest } = this.props;
    const { activeTemplate } = this.state;
    const data = {
      ...activeTemplate,
      logoId: null
    };
    editTemplate(userId, data).then(() => {
      getTemplatest(userId);
    });
    this.setState({
      activeTemplate: {
        ...data
      }
    });
  };

  onDownloadTemplate = template => {
    const t = cloneDeep(template);
    // have error withoun encode to base64
    t.text.opening = btoa(encodeURIComponent(t.text.opening));
    t.text.closing = btoa(encodeURIComponent(t.text.closing));
    const filename = `${t.title || "template"}.csv`;
    json2csv([t], (err, csv) => {
      if (err) console.error(csv);
      fileDownload(csv, filename, "text/csv");
    });
  };

  setLogo = logoId => {
    const { activeTemplate } = this.state;
    const { editTemplate, userId, getTemplatest } = this.props;
    const data = {
      ...activeTemplate,
      logoId
    };
    editTemplate(userId, data).then(() => {
      getTemplatest(userId);
    });
    this.setState({ activeTemplate: data });
  };

  onEditTemplate = ({ title }) => {
    const { editTemplate, userId, getTemplatest } = this.props;
    const { activeTemplateByEditTitle, activeTemplate } = this.state;

    const data = {
      ...activeTemplateByEditTitle,
      title
    };
    editTemplate(userId, data).then(() => {
      getTemplatest(userId);
    });

    if (activeTemplateByEditTitle.id === activeTemplate.id) {
      this.setState({ activeTemplate: { ...activeTemplate, title } });
    }
  };

  onDelete = id => {
    const { deleteTemplate, userId, getTemplatest } = this.props;
    deleteTemplate(userId, id).then(() => {
      getTemplatest(userId);
    });
  };

  onCopy = template => {
    const { copyTemplate, userId, getTemplatest } = this.props;
    copyTemplate(userId, template).then(() => {
      getTemplatest(userId);
    });
  };

  setFieldsToActiveTemplate = reportFields => {
    const { activeTemplate } = this.state;
    this.setState({ activeTemplate: { ...activeTemplate, reportFields } });
  };

  onActiveTemplateByEditTitle = template => {
    this.setState({ activeTemplateByEditTitle: template });
  };

  clearActiveTemplateByEdit = () => {
    this.setState({ activeTemplateByEditTitle: null });
  };

  onActivateTemplate = template => {
    this.setState({ activeTemplate: template });
  };

  onActiveCategory = category => {
    this.setState({ category });
  };

  onActiveType = async e => {
    const { userId, editTemplateType, getTemplatest } = this.props;
    const { activeTemplate } = this.state;
    const typeReport = e.target.value;
    this.setState({
      activeTemplate: { ...this.state.activeTemplate, typeReport }
    });
    await editTemplateType(userId, activeTemplate.id, typeReport).then(() => {
      getTemplatest(userId);
    });
    return typeReport;
  };

  onSave = html => {
    const { editTemplate, userId, getTemplatest } = this.props;
    const { activeTemplate, category } = this.state;

    const data = {
      ...activeTemplate,
      text: { ...activeTemplate.text, [category]: html }
    };

    editTemplate(userId, data).then(() => {
      getTemplatest(userId);
    });
    this.setState({
      activeTemplate: {
        ...data
      }
    });
  };

  _renderTabs = () => {
    const { category, activeTemplate } = this.state;
    const { uploadImage } = this.props;

    const executor = {
      title: (
        <TitleEditor
          onSave={this.onSave}
          value={activeTemplate.text[category]}
        />
      ),
      opening: (
        <TemplatesEditor
          onSave={this.onSave}
          item={activeTemplate.text[category]}
          onUpload={uploadImage}
        />
      ),
      closing: (
        <TemplatesEditor
          onSave={this.onSave}
          item={activeTemplate.text[category]}
          onUpload={uploadImage}
        />
      ),
      format: (
        <TemplatesSettings
          onActiveType={this.onActiveType}
          typeReport={activeTemplate.typeReport}
          types={REPORT_TYPES}
          setLogo={this.setLogo}
          logoId={activeTemplate.logoId}
          deleteLogo={this.deleteImage}
          defaultFields={FIELDS_BY_TYPE}
          reportFields={
            activeTemplate.reportFields ||
            FIELDS_BY_TYPE[activeTemplate.typeReport]
          }
          templateId={activeTemplate.id}
          setFieldsToActiveTemplate={this.setFieldsToActiveTemplate}
        />
      )
    };
    return executor[category];
  };

  render() {
    const { templates } = this.props;
    const { activeTemplate, category, activeTemplateByEditTitle } = this.state;

    const hideList =
      Object.keys((templates && templates.list) || {}).length <= 0;
    return (
      <div className="templates-container">
        {!hideList && (
          <React.Fragment>
            {!!activeTemplate && (
              <TemplatesMenuCategories
                category={category}
                onActive={this.onActiveCategory}
              />
            )}
            {!hideList && (
              <TemplateList
                onDownloadTemplate={this.onDownloadTemplate}
                onActiveEdit={this.onActiveTemplateByEditTitle}
                templates={templates}
                onActive={this.onActivateTemplate}
                onDelete={this.onDelete}
                onCopy={this.onCopy}
                activeId={activeTemplate && activeTemplate.id}
              />
            )}
            {!!activeTemplate && this._renderTabs()}
          </React.Fragment>
        )}
        <TemplateCreator
          activeItem={activeTemplateByEditTitle}
          onClose={this.clearActiveTemplateByEdit}
          onAdd={this.onAddTemplate}
          onEdit={this.onEditTemplate}
          types={REPORT_TYPES}
        />
      </div>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Templates);
